<template>
  <v-row justify="center" align="center">
    <v-col cols="12" md="10" lg="6" class="pa-4">
      <v-card color="background" class="px-4 py-6">
        <v-row no-gutters align="center">
          <v-col cols="auto">
            <v-btn @click="goToEmail(); clearError(); password = ''" icon color="secondary"><v-icon>mdi-arrow-left</v-icon></v-btn>
          </v-col>
          <v-col class="text-center">
            <span class="text-body-1 font-weight-bold" v-if="activated">Enter your password</span>
            <span class="text-body-1 font-weight-bold" v-else-if="!activated && !isNew">Enter your registration code</span>
            <span class="text-body-1 font-weight-bold" v-if="!activated && isNew">Enter your new password</span>
          </v-col>
          <v-col cols="auto">
            <v-sheet width="36" height="36" color="transparent"></v-sheet>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <LoginError :error="error" />
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="12" class="pb-0">
            <v-form v-if="!isNew" @submit.prevent="checkPassword(password)">
              <v-text-field
                  autofocus
                  v-if="activated"
                  class="custom-field"
                  :placeholder="'Password'"
                  persistent-placeholder
                  variant="outlined"
                  bg-color="#ffffff"
                  :append-icon="value ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="() => (value = !value)"
                  :type="value ? 'password' : 'text'"
                  v-model="password"
                  :rules="[v => !!v && !!v.length]"
                  @update:modelValue="clearError"
                  autocomplete="current-password">
              </v-text-field>
              <v-text-field
                  v-else
                  class="custom-field"
                  placeholder="Registration code"
                  persistent-placeholder
                  variant="outlined"
                  bg-color="#ffffff"
                  v-model="code"
                  @update:modelValue="clearError"
                  autocomplete="new-password">
              </v-text-field>
            </v-form>

            <v-form v-if="isNew" v-model="passwordValid" ref="passwordForm" @submit.prevent="checkPassword(password)">
              <v-text-field
                  autofocus
                  class="custom-field"
                  placeholder="New password"
                  persistent-placeholder
                  variant="outlined"
                  bg-color="#ffffff"
                  :append-icon="value['new'] ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="() => (value['new'] = !value['new'])"
                  :type="value['new'] ? 'password' : 'text'"
                  @update:modelValue="$refs.repeatPasswordForm.validate()"
                  v-model="password"
                  :rules="[
                      (v) => (v && v.length >= 6) || 'Password must be at least 6 characters long.',
                      (v) => (v && v.toUpperCase() !== v) || 'Password must contain a lowercase character.',
                      (v) => (v && v.toLowerCase() !== v) || 'Password must contain an uppercase character.',
                      (v) => (v && /\d/.test(v)) || 'Password must contain a number.',
                      (v) => (v && symbols.test(v)) || 'Password must contain a special character.',
                  ]"
                  autocomplete="new-password"></v-text-field>
            </v-form>
            <v-form v-if="isNew" v-model="repeatValid" ref="repeatPasswordForm" @submit.prevent="checkPassword(password)">
              <v-text-field
                  class="custom-field"
                  placeholder="Repeat new password"
                  persistent-placeholder
                  variant="outlined"
                  bg-color="#ffffff"
                  :append-icon="value['repeat'] ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="() => (value['repeat'] = !value['repeat'])"
                  :type="value['repeat'] ? 'password' : 'text'"
                  v-model="repeatedPassword"
                  @update:modelValue="$refs.repeatPasswordForm.validate()"
                  :rules="[
                      (v) => !passwordValid || v === password || 'Passwords do not match.'
                  ]"
                  autocomplete="new-password"></v-text-field>
            </v-form>
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="12" class="text-center pt-0">
            <a @click="goToForgotPassword()" style="color: #f2243f" v-if="activated">Forgot your password?</a>
            <div v-else-if="!isNew">Don't have your registration code? <br>  <a @click="goToResendRegistration()" style="color: #f2243f">Resend it.</a> </div>
          </v-col>
          <v-col class="d-flex justify-center">
            <ActionButton width="95" @click="checkPassword(password)" text="Confirm" color="secondary" :loading="loading" :disabled="confirmedDisabled || loading"></ActionButton>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import {Component, Vue, toNative, Prop, Watch, Emit} from "vue-facing-decorator";
import ActionButton from "@/components/ActionButton.vue";
import LoginError from "@/components/LoginError.vue";
@Component({
  components: {LoginError, ActionButton}
})
class PasswordPrompt extends Vue {
  @Prop() private error!: string;
  @Prop() private activated!: boolean;
  @Prop({default: false}) private isNew!: boolean;
  @Prop() private queryCode!: string;
  @Prop() private loading!: boolean;
  private symbols = /^(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])/;
  private code = '';
  private password = '';
  private repeatedPassword = '';
  private value = {
    repeat: true,
    new: true,
  };
  private passwordValid = true;
  private repeatValid = true;

  private get confirmedDisabled() {
    if (this.isNew) {
      if (this.activated) {
        return !this.code;
      } else {
        return !this.passwordValid || !this.repeatValid || !this.password || !this.repeatedPassword;
      }
    } else {
      return !this.password;
    }
  }

  @Emit()
  private clearError() {
    return true;
  }
  @Emit()
  private goToEmail() {
    return true;
  }
  @Emit()
  private goToForgotPassword() {
    return true;
  }
  @Emit()
  private goToResendRegistration() {
    return true;
  }
  @Emit()
  private checkPassword(password: string): string {
    return password;
  }
  @Watch('queryCode', {immediate: true})
  private onQueryCodeUpdate() {
    this.code = this.queryCode
  }

  @Watch('code', {immediate: true})
  private onCodeUpdate() {
    this.password = this.code
  }

}

export default toNative(PasswordPrompt);
</script>

<style scoped>

</style>